import { useState, useEffect, useMemo, useCallback } from 'react';

import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

import { SuccessToast } from '@src/components';

import { useDisableAfterBuffer, useSendWebinarInvitation } from '@src/hooks';
import { usePostWebinarAttendeeAction, useRoomQuery, useWebinarAttendeesQuery } from '@src/hooks/queries';
import { queryKeys } from '@src/utils/queryKeys';
import { fmtRole } from '@src/utils';

import {
  WEBINAR_ROLE_MODAL,
  PACKAGE_LIMIT_MESSAGE,
  PANEL_LIST_CAPACITY_MESSAGE,
  WEBINAR_STARTED_CHANGE_DISABLED,
} from '@src/constants';

import { IAttendee, ISendInviteResponse } from '@src/webinar/types';
import { EUserType, TUserTypeWithoutHost, TWebinarStatus } from '@src/types';

export const useWebinarActionCell = (attendee: IAttendee, roomId: string | undefined) => {
  const [loadingStatus, setLoadingStatus] = useState<'first' | 'second' | null>(null);
  const [inviteRole, setInviteRole] = useState<TUserTypeWithoutHost | null>(null);
  const [attendeesIdList, setAttendeesIdList] = useState<Set<string>>(new Set());
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    description: string;
    role: TUserTypeWithoutHost;
  } | null>(null);

  const { data: participants } = useWebinarAttendeesQuery(roomId);
  const { data: roomInfo } = useRoomQuery(roomId || '');
  const queryClient = useQueryClient();
  const { mutate: postAction, isPending: isWebinarActionPending } = usePostWebinarAttendeeAction();
  const { mutate: sendWebinarInvitation, isPending: isSendingInvitation } = useSendWebinarInvitation({
    isReinvite: true,
    onSuccess: (response: ISendInviteResponse) => {
      setModalConfig(null);
      toast(<SuccessToast message={`${response.message}`} />, {
        className: 'custom-toast-wrapper',
        autoClose: 3000,
        closeButton: false,
        hideProgressBar: true,
        icon: false,
      });
    },
    onError: (error) => {
      if (attendee?._id && error.includes('already joined')) {
        setAttendeesIdList?.((prev) => {
          const newSet = new Set(prev);
          newSet.add(attendee._id);
          return newSet;
        });
        setModalConfig(null);
      }
    },
  });

  const [currentStatus, setCurrentStatus] = useState<TWebinarStatus>(attendee.status);

  const isManagementDisabled = useDisableAfterBuffer(roomInfo?.startTime);

  useEffect(() => {
    setCurrentStatus(attendee.status);
  }, [attendee.status]);

  const handleStatusChange = useCallback(
    (newStatus: Exclude<TWebinarStatus, 'PENDING'>, buttonType?: 'first' | 'second') => {
      setLoadingStatus(buttonType || null);
      postAction(
        { status: newStatus, attendees: [attendee] },
        {
          onSuccess: () => {
            setCurrentStatus(newStatus);
            queryClient.invalidateQueries({
              queryKey: [queryKeys.webinar.attendees(roomId ?? '')],
            });
            queryClient.invalidateQueries({
              queryKey: [queryKeys.webinar.attendeesPaginated(roomId ?? '')],
            });
          },
          onError: () => setLoadingStatus(null),
        }
      );
    },
    [attendee, postAction, queryClient, roomId]
  );

  const { panelFull, attendanceFull } = useMemo(() => {
    const panelLimit = participants?.package?.panelistsLimit ?? 0;
    const attendanceLimit = participants?.package?.attendanceLimit ?? 0;
    const numCoHosts = participants?.panelList?.co_hosts ?? 0;
    const numSpeakers = participants?.panelList?.speakers ?? 0;
    const numApproved = participants?.attendance?.approved ?? 0;
    return {
      panelFull: numCoHosts + numSpeakers >= panelLimit,
      attendanceFull: numApproved >= attendanceLimit,
    };
  }, [participants]);

  const openRoleModal = useCallback(
    (toRole: TUserTypeWithoutHost) => {
      const fromRole = attendee.type;
      const { title = '', desc = '' } = WEBINAR_ROLE_MODAL[fromRole]?.[toRole] || {};
      setModalConfig({ title, description: desc, role: toRole });
    },
    [attendee.type]
  );

  const sendRoleChangeNotification = useCallback(
    (role: TUserTypeWithoutHost) => {
      if (role === EUserType.ATTENDEE) {
        sendWebinarInvitation({
          roomId: roomId!,
          userType: role,
          recipients: [attendee.email],
          emails: [attendee.email],
        });
      } else {
        setInviteRole(role);
        setModalConfig(null);
      }
    },
    [attendee.email, roomId, sendWebinarInvitation]
  );

  const dropdownItems = useMemo(() => {
    const approveReject =
      currentStatus === 'APPROVED'
        ? { label: 'Reject', command: () => handleStatusChange('REJECTED') }
        : currentStatus === 'REJECTED'
          ? {
              label: 'Approve',
              command: () => handleStatusChange('APPROVED'),
              disabled: attendee.type === EUserType.ATTENDEE ? attendanceFull : panelFull,
              tooltip:
                attendee.type === EUserType.ATTENDEE
                  ? attendanceFull
                    ? PACKAGE_LIMIT_MESSAGE
                    : ''
                  : panelFull
                    ? PANEL_LIST_CAPACITY_MESSAGE
                    : '',
            }
          : null;

    const roleActions = [];
    if (attendee.type === EUserType.ATTENDEE) {
      roleActions.push(
        {
          label: 'Promote to Co-host',
          command: () => openRoleModal(EUserType.CO_HOST),
          disabled: panelFull,
          tooltip: panelFull ? PANEL_LIST_CAPACITY_MESSAGE : '',
        },
        {
          label: 'Promote to Speaker',
          command: () => openRoleModal(EUserType.SPEAKER),
          disabled: panelFull,
          tooltip: panelFull ? PANEL_LIST_CAPACITY_MESSAGE : '',
        }
      );
    } else {
      const otherRoles: TUserTypeWithoutHost[] = [EUserType.ATTENDEE, EUserType.CO_HOST, EUserType.SPEAKER].filter(
        (role) => role !== attendee.type
      ) as TUserTypeWithoutHost[];
      otherRoles.forEach((role) => {
        const actionCellTooltip = {
          disabled: false,
          tooltip: '',
        };

        if (role === EUserType.ATTENDEE && attendanceFull) {
          actionCellTooltip.disabled = true;
          actionCellTooltip.tooltip = PACKAGE_LIMIT_MESSAGE;
        } else if (
          (role === EUserType.CO_HOST || role === EUserType.SPEAKER) &&
          currentStatus === 'REJECTED' &&
          panelFull
        ) {
          actionCellTooltip.disabled = true;
          actionCellTooltip.tooltip = PANEL_LIST_CAPACITY_MESSAGE;
        }

        roleActions.push({
          label: `Change to ${fmtRole(role).replace('_', '-')}`,
          command: () => openRoleModal(role),
          ...actionCellTooltip,
        });
      });
    }

    return [...roleActions, ...(approveReject ? [approveReject] : [])];
  }, [attendee.type, currentStatus, handleStatusChange, openRoleModal, panelFull, attendanceFull]);

  const { isActionDisabled, actionTooltip, isMainButtonDisabled } = useMemo(() => {
    if (isManagementDisabled) {
      return {
        isActionDisabled: true,
        actionTooltip: WEBINAR_STARTED_CHANGE_DISABLED,
        isMainButtonDisabled: true,
      };
    }

    const isPending = currentStatus === 'PENDING';
    const isActionDisabled = isPending && attendanceFull && panelFull;
    const isMainButtonDisabled = isPending && attendanceFull;
    const actionTooltip = isMainButtonDisabled ? PACKAGE_LIMIT_MESSAGE : '';

    return { isActionDisabled, actionTooltip, isMainButtonDisabled };
  }, [isManagementDisabled, currentStatus, attendanceFull, panelFull]);

  return {
    currentStatus,
    loadingStatus,
    inviteRole,
    modalConfig,
    setModalConfig,
    setInviteRole,
    handleStatusChange,
    dropdownItems,
    attendanceFull,
    panelFull,
    sendRoleChangeNotification,
    isSendingInvitation,
    isWebinarActionPending,
    fmtRole,
    isActionDisabled,
    actionTooltip,
    isMainButtonDisabled,
    attendeesIdList,
    setAttendeesIdList,
  };
};
